import { type UiI18n } from '@myre/domain-i18n-wordings';
import { DateFormatPipe } from '@myre/ui-primitive-core';
import { isPeriodPickerValue } from '@myre/ui-primitive-forms';

import { inject, Pipe, type PipeTransform } from '@angular/core';

import { WORDINGS } from '../../../../../myre-wordings-loader';

import * as FilterHelper from '../../helpers/filter.helper';
import {
  type Filter,
  type FilterValue,
} from '../../interfaces/filters/base.filter';

@Pipe({
  name: 'tagLabel',
  standalone: true,
})
export class TagLabelPipe implements PipeTransform {
  protected _dateFormatPipe = inject(DateFormatPipe);

  protected readonly _wordings = inject<UiI18n>(WORDINGS);

  public transform(
    selected: FilterValue | null,
    filter: Filter<string>,
  ): string {
    if (!FilterHelper.isSelectedOption(selected)) {
      return '';
    }

    if (
      isPeriodPickerValue(selected) &&
      FilterHelper.isPeriodPickerFilter(filter)
    ) {
      const prefix = (filter.types ?? []).find(
        type => type.value === selected.type,
      )?.label;
      const postfix = [selected.range.from, selected.range.to]
        .map(d => this._dateFormatPipe.transform(d))
        .filter(Boolean)
        .join(' - ');

      return [prefix, postfix].filter(Boolean).join(' — ');
    }

    // If the selected value is not an array, we return the label of the selected value
    if (!Array.isArray(selected)) {
      return this._qualify(this._getLabelForValue(selected, filter), filter);
    }

    // If the selected value has a length of 1, we return the label of the only selected value
    if (selected.length === 1) {
      return this._qualify(this._getLabelForValue(selected[0], filter), filter);
    }

    // If the selected value has a length greater than 1, we return the number of selected values and the filter label
    return [`${selected.length}`, 'label' in filter ? filter.label : '']
      .filter(Boolean)
      .join(' ');
  }

  /**
   * A single-choice filter collapses to its sole option, whose wording is often a bare
   * `Yes` / `No` that says nothing about what it narrows — the tag is then the only place
   * left carrying the filter name, since the dropdown placeholder is gone.
   */
  private _qualify(optionLabel: string, filter: Filter<string>): string {
    const filterLabel = 'label' in filter ? filter.label : '';

    if (filter.type !== 'select' || !filterLabel || !optionLabel) {
      return optionLabel;
    }

    return this._wordings._ui.filterListPaginated.filter.tagLabel({
      bindings: { label: filterLabel, value: optionLabel },
    });
  }

  private _getLabelForValue(
    value: FilterValue,
    filter: Filter<string>,
  ): string {
    if (FilterHelper.isFilter(filter)) {
      return (
        filter.options?.find(choice => choice.value === value)?.label ?? ''
      );
    }

    return '';
  }
}
